import React, { useEffect } from 'react';
import { AlertTriangle, Info } from 'lucide-react';

interface ConfirmDialogProps {
  type: 'alert' | 'confirm';
  title?: string;
  message: string;
  dialogType?: 'danger' | 'warning' | 'info';
  onClose: (value: boolean) => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  type,
  title,
  message,
  dialogType = 'warning',
  onClose,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose(false);
      } else if (e.key === 'Enter' && type === 'alert') {
        onClose(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [type, onClose]);

  const getIcon = () => {
    switch (dialogType) {
      case 'danger':
        return <AlertTriangle size={48} style={{ color: '#ef4444' }} />;
      case 'warning':
        return <AlertTriangle size={48} style={{ color: '#f59e0b' }} />;
      case 'info':
      default:
        return <Info size={48} style={{ color: '#3b82f6' }} />;
    }
  };

  const getButtonStyles = () => {
    switch (dialogType) {
      case 'danger':
        return {
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#ef4444',
          hoverBg: 'rgba(239, 68, 68, 0.25)',
        };
      case 'warning':
        return {
          background: 'rgba(245, 158, 11, 0.15)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          color: '#f59e0b',
          hoverBg: 'rgba(245, 158, 11, 0.25)',
        };
      case 'info':
      default:
        return {
          background: 'var(--accent-gradient)',
          border: 'none',
          color: '#fff',
          hoverBg: 'var(--accent-gradient)',
        };
    }
  };

  const btnStyles = getButtonStyles();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px',
      }}
      onClick={() => onClose(false)}
    >
      <div
        className="glass-panel"
        style={{
          maxWidth: '420px',
          width: '100%',
          padding: '24px',
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          background: '#0f172a',
          animation: 'confirmZoomIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes confirmZoomIn {
            from { transform: scale(0.95); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        `}</style>
        
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          {getIcon()}
        </div>

        <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px', color: '#fff' }}>
          {title || (type === 'confirm' ? 'Are you sure?' : 'Notification')}
        </h3>

        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: 1.5 }}>
          {message}
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          {type === 'confirm' && (
            <button
              onClick={() => onClose(false)}
              className="btn btn-secondary"
              style={{ padding: '8px 16px', borderRadius: '24px', fontWeight: 600, fontSize: '14px' }}
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => onClose(true)}
            className="btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: btnStyles.background,
              border: btnStyles.border,
              color: btnStyles.color,
              padding: '8px 20px',
              borderRadius: '24px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <span>{type === 'confirm' ? 'Confirm' : 'OK'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
